/**
 * Switches between the table and the board (F2).
 *
 * The choice is written to the URL, so a shared link opens on the view the
 * sender was looking at — and a reload does not silently switch someone back
 * to a view they had left.
 *
 * A radio group rather than two buttons: the two options are mutually
 * exclusive and one is always active, which is what a radio group means and
 * what a screen reader then announces.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.2
 */
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../components/ui/Icon';
import { cn } from '../../../lib/cn';

import { VIEWS, VIEW_ICON, type PipelineView } from './pipeline-views';

export interface ViewToggleProps {
  view: PipelineView;
  onViewChange: (view: PipelineView) => void;
}

export function ViewToggle({ view, onViewChange }: ViewToggleProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <fieldset className="border-gray-200 flex rounded-md border p-0.5">
      <legend className="sr-only">{t('startups.view.label')}</legend>

      {VIEWS.map((candidate) => (
        <label
          key={candidate}
          className={cn(
            'flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-3 text-sm',
            view === candidate
              ? 'bg-blue-light text-blue-strong font-semibold'
              : 'text-body',
          )}
        >
          <input
            type="radio"
            name="pipeline-view"
            value={candidate}
            checked={view === candidate}
            onChange={() => {
              onViewChange(candidate);
            }}
            /* Visually hidden rather than removed: a hidden input is
               unfocusable, which would take the group out of the tab order. */
            className="sr-only"
          />
          <Icon name={VIEW_ICON[candidate]} />
          {t(`startups.view.${candidate}`)}
        </label>
      ))}
    </fieldset>
  );
}
